import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    TextField,
    IconButton,
    Button,
    Paper,
    List,
    ListItem,
    ListItemText,
    Grid,
    Checkbox,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEffect, useState, useCallback } from "react";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { formattaFloat } from "../utils/globalUtils";
import {
    createVariant,
    deleteVariant,
    getAllVariants,
    setVariantProducts,
    updateVariant,
} from "../APIs/VariantsAPI";

const getVariantProductIds = (variant) =>
    (Array.isArray(variant?.products) ? variant.products : [])
        .map((entry) => Number(entry?.productId ?? entry?.product?.id))
        .filter((id) => Number.isInteger(id) && id > 0);

const ModalVariantList = ({ open, onClose, product }) => {
    const [variants, setVariants] = useState([]);
    const [linkedVariantIds, setLinkedVariantIds] = useState([]);
    const { t } = useTranslation();
    const tenant = localStorage.getItem('tenantId');
    const token = localStorage.getItem('accessToken');

    const [openAddVariant, setOpenAddVariant] = useState(false);
    const [openEditVariant, setOpenEditVariant] = useState(false);
    const [openDeleteVariant, setOpenDeleteVariant] = useState(false);
    const [variantId, setVariantId] = useState();
    const [variantName, setVariantName] = useState("");
    const [variantPrice, setVariantPrice] = useState(product?.price || 0);
    const [variantDesc, setVariantDesc] = useState("");

    const fetchVarianti = useCallback(async () => {
        const data = await getAllVariants(token, tenant);
        const normalized = Array.isArray(data) ? data : [];
        setVariants(normalized);
        setLinkedVariantIds(
            normalized
                .filter((variant) => getVariantProductIds(variant).includes(Number(product?.id)))
                .map((variant) => Number(variant.id))
        );
    }, [product?.id, tenant, token]);

    useEffect(() => {
        if (!open) return;
        fetchVarianti().catch((err) => console.error("Errore nel caricamento varianti:", err));
    }, [open, fetchVarianti]);

    function resetModal() {
        setVariantName("");
        setVariantPrice(product?.price || 0);
        setVariantDesc("");
        setVariantId();
        setOpenAddVariant(false);
        setOpenEditVariant(false);
        setOpenDeleteVariant(false);
    }

    function handleOpenEdit(variant) {
        setVariantName(variant.name);
        setVariantPrice(variant.price);
        setVariantDesc(variant.description || "");
        setVariantId(variant.id);
        setOpenEditVariant(true);
    }

    function handleOpenDelete(selectedVariantId) {
        setVariantId(selectedVariantId);
        setOpenDeleteVariant(true);
    }

    async function handleCreateSubmit() {
        await createVariant({
            name: variantName,
            description: variantDesc,
            price: formattaFloat(variantPrice),
            productIds: product?.id ? [Number(product.id)] : [],
        }, token, tenant);
        resetModal();
        await fetchVarianti();
    }

    async function handleEditSubmit() {
        const currentVariant = variants.find((variant) => Number(variant.id) === Number(variantId));
        await updateVariant(variantId, {
            name: variantName,
            description: variantDesc,
            price: formattaFloat(variantPrice),
            productIds: getVariantProductIds(currentVariant),
        }, token, tenant);
        resetModal();
        await fetchVarianti();
    }

    async function handleDeleteSubmit() {
        await deleteVariant(variantId, token, tenant);
        resetModal();
        await fetchVarianti();
    }

    async function handleToggleLink(selectedVariantId, checked) {
        const currentVariant = variants.find((variant) => Number(variant.id) === Number(selectedVariantId));
        const productIds = new Set(getVariantProductIds(currentVariant));
        if (checked) productIds.add(Number(product?.id));
        else productIds.delete(Number(product?.id));

        await setVariantProducts(selectedVariantId, [...productIds], token, tenant);
        await fetchVarianti();
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" gap={2} alignItems="center">
                    <Box>
                        <Typography variant="h5">{t("variantManagement.title")}</Typography>
                        <Typography variant="body2" color="text.secondary">{product?.name || ""}</Typography>
                    </Box>
                    <Button variant="contained" onClick={() => setOpenAddVariant(true)}>{t("variantManagement.addVariant")}</Button>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Paper elevation={2} sx={{ maxWidth: "100%", overflow: "hidden" }}>
                    {variants.length === 0 ? (
                        <Typography sx={{ p: 2 }}>{t('no_variants')}</Typography>
                    ) : (
                        <List>
                            {variants.map((variant) => {
                                const linked = linkedVariantIds.map(Number).includes(Number(variant.id));
                                return (
                                    <ListItem
                                        key={variant.id}
                                        divider
                                        sx={{
                                            alignItems: { xs: "stretch", sm: "flex-start" },
                                            display: "flex",
                                            gap: { xs: 1, sm: 1.5 },
                                            flexDirection: { xs: "column", sm: "row" },
                                            px: { xs: 1.5, sm: 2 },
                                            py: { xs: 1.5, sm: 1 },
                                        }}
                                    >
                                        <Box display="flex" gap={1} alignItems="flex-start" sx={{ minWidth: 0, flex: "1 1 auto" }}>
                                            <Checkbox
                                                checked={linked}
                                                onChange={(event) => handleToggleLink(variant.id, event.target.checked)}
                                                inputProps={{ "aria-label": t("variantManagement.linkProducts") }}
                                                sx={{ mt: 0.5, flexShrink: 0 }}
                                            />
                                            <ListItemText
                                                primary={variant.name}
                                                sx={{ minWidth: 0, my: 0 }}
                                                primaryTypographyProps={{
                                                    sx: { overflowWrap: "anywhere", fontWeight: 700 },
                                                }}
                                                secondary={(
                                                    <>
                                                        <Typography component="span" display="block" sx={{ overflowWrap: "anywhere" }}>
                                                            {`${t('price')}: ${parseFloat(variant.price).toFixed(2)} EUR`}
                                                        </Typography>
                                                        {variant.description ? (
                                                            <Typography component="span" display="block" sx={{ overflowWrap: "anywhere" }}>{variant.description}</Typography>
                                                        ) : null}
                                                        <Typography component="span" display="block" variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                                                            {linked ? t("variantManagement.linkedToProduct") : t("variantManagement.notLinkedToProduct")}
                                                        </Typography>
                                                    </>
                                                )}
                                            />
                                        </Box>
                                        <Box
                                            display="flex"
                                            gap={0.5}
                                            justifyContent={{ xs: "flex-end", sm: "flex-start" }}
                                            alignItems="center"
                                            sx={{ flexShrink: 0, alignSelf: { xs: "stretch", sm: "center" } }}
                                        >
                                            <IconButton size="small" onClick={() => handleOpenEdit(variant)} aria-label={t("edit_product")}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size='small' onClick={() => handleOpenDelete(variant.id)} aria-label={t("delete_button")}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                    {openAddVariant && (
                        <Dialog open={openAddVariant} onClose={resetModal}>
                            <DialogTitle>{t("modalVariant.addVariant")}</DialogTitle>
                            <DialogContent>
                                <Grid container sx={{ p: 1 }} spacing={1}>
                                    <Grid item xs={12}>
                                        <TextField disabled fullWidth value={product?.name || ""} label={t("product")} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth type="text" value={variantName} onChange={(e) => setVariantName(e.target.value)} label={t("modalVariant.variantName")} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth type="number" value={variantPrice} onChange={(e) => setVariantPrice(e.target.value)} label={t("modalVariant.variantPrice")} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth type="text" value={variantDesc} onChange={(e) => setVariantDesc(e.target.value)} label={t("modalVariant.variantDesc")} />
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <DialogActions>
                                <Button variant="contained" onClick={resetModal}>{t("confirmOperation.cancel")}</Button>
                                <Button variant="contained" onClick={handleCreateSubmit} disabled={!variantName.trim()}>{t("confirmOperation.confirm")}</Button>
                            </DialogActions>
                        </Dialog>
                    )}
                    {openEditVariant && (
                        <Dialog open={openEditVariant} onClose={resetModal}>
                            <DialogTitle>{t("modalVariant.editVariant")}</DialogTitle>
                            <DialogContent>
                                <Grid container sx={{ p: 1 }} spacing={1}>
                                    <Grid item xs={12}>
                                        <TextField disabled fullWidth value={product?.name || ""} label={t("product")} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth type="text" value={variantName} onChange={(e) => setVariantName(e.target.value)} label={t("modalVariant.variantName")} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField fullWidth type="number" value={variantPrice} onChange={(e) => setVariantPrice(e.target.value)} label={t("modalVariant.variantPrice")} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth type="text" value={variantDesc} onChange={(e) => setVariantDesc(e.target.value)} label={t("modalVariant.variantDesc")} />
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <DialogActions>
                                <Button variant="contained" onClick={resetModal}>{t("confirmOperation.cancel")}</Button>
                                <Button variant="contained" onClick={handleEditSubmit} disabled={!variantName.trim()}>{t("confirmOperation.confirm")}</Button>
                            </DialogActions>
                        </Dialog>
                    )}
                    {openDeleteVariant && (
                        <Dialog open={openDeleteVariant} onClose={resetModal}>
                            <DialogTitle>{t("modalVariant.deleteVariant")}</DialogTitle>
                            <DialogContent>{t("modalVariant.delete_note")}</DialogContent>
                            <DialogActions>
                                <Button variant="contained" onClick={resetModal}>{t("confirmOperation.cancel")}</Button>
                                <Button variant="contained" onClick={handleDeleteSubmit}>{t("confirmOperation.confirm")}</Button>
                            </DialogActions>
                        </Dialog>
                    )}
                </Paper>
            </DialogContent>
        </Dialog>
    );
};

export default ModalVariantList;
